// Globális dátumválasztó (Popover + Calendar). Drop-in a natív `<input type="date">` helyére: a VÁLTOZATLAN
// szerződés `YYYY-MM-DD` string (üres = nincs kiválasztva) — így a lista-eszköztár dátum-szűrői (from/to) és
// bármely form ugyanúgy fogyasztja. A megjelenített formátum locale-tudatos; törlés a beépített „×"-szel.
//
// A LÁTHATÓ ÜRES-SZÖVEG és az A11Y-NÉV KÜLÖN prop (`placeholder` vs `label`): ahol a mező FELETT már ott a
// `FormLabel` (form-mezők), ott a címke MEGISMÉTLÉSE zaj — a `DateField` ezért formátum-HINTET ad
// placeholderként („ÉÉÉÉ. HH. NN."). Ahol nincs külön címke (lista-eszköztár dátum-szűrői), ott a `label`
// marad a látható szöveg is — ezért a `placeholder` elhagyható, és a fallback a `label`.

import { CalendarIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { format, isValid, parseISO } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface DatePickerProps {
  /** `YYYY-MM-DD` vagy üres string. */
  value: string;
  onChange: (value: string) => void;
  /** A11y-név (pl. „Ettől") — és `placeholder` nélkül a látható üres-szöveg is. Explicit `undefined` is OK. */
  label?: string | undefined;
  /** A látható üres-szöveg, ha a mezőnek van külön címkéje (form): formátum-hint, NEM a címke ismétlése. */
  placeholder?: string | undefined;
  /** UI-nyelv a megjelenített dátum-formátumhoz; alap `hu`. */
  language?: string;
  /** Zárolt mező (pl. már számlázott hozzárendelés dátuma) — nem nyitható, nem törölhető. */
  disabled?: boolean;
}

/** `YYYY-MM-DD` → Date (helyi éjfél) | null. */
function toDate(value: string): Date | null {
  if (!value) return null;
  const d = parseISO(value);
  return isValid(d) ? d : null;
}

export function DatePicker({
  value,
  onChange,
  label,
  placeholder,
  language = 'hu',
  disabled = false,
}: DatePickerProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selected = toDate(value);
  const displayFmt = language.toLowerCase().startsWith('en') ? 'yyyy-MM-dd' : 'yyyy. MM. dd.';

  const showClear = selected !== null && !disabled;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* A törlés VALÓDI `<button>`, a triggerrel TESTVÉR — nem beágyazott (a `<button>` a `<button>`-ben
          érvénytelen HTML), és nem `role="button"`-os SVG. A korábbi alak a Heroicons ikonra tett
          `role="button"` + `aria-label` párost, DE a Heroicons `aria-hidden="true"`-t is ad → az elem sem a
          a11y-fában nem volt, sem Tabbal nem érhető el. Mérve 2026-08-19-én, a TimePicker tesztjén bukott ki. */}
      <div className="relative w-full">
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={label}
            disabled={disabled}
            className={cn(
              'border-border input-glass ease-atlas text-md flex h-8 w-full items-center gap-2 rounded-lg border px-2.5 py-1 transition-[border-color,box-shadow] duration-150',
              'focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
              showClear && 'pr-8',
            )}
          >
            <CalendarIcon className="text-muted-foreground size-4 shrink-0" />
            <span className={cn('min-w-0 flex-1 truncate text-left', !selected && 'text-subtle')}>
              {selected ? format(selected, displayFmt) : (placeholder ?? label ?? '')}
            </span>
          </button>
        </PopoverTrigger>
        {showClear && (
          <button
            type="button"
            aria-label={t('common.clear')}
            onClick={() => onChange('')}
            className="text-muted-foreground hover:text-foreground focus-visible:ring-accent-600 absolute right-1.5 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center rounded focus-visible:outline-none focus-visible:ring-2"
          >
            <XMarkIcon className="size-4" />
          </button>
        )}
      </div>
      {/* portal={false}: modális panelen belül is kattintható/görgethető maradjon (lásd a `TimePicker`-t). */}
      <PopoverContent align="start" portal={false} className="w-auto">
        <Calendar
          selected={selected}
          language={language}
          onSelect={(d) => {
            onChange(format(d, 'yyyy-MM-dd'));
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
